/**
 * Wall rendering geometry: miter joints, opening subtraction, the ranked snap
 * engine, and the view/calibration transforms.
 *
 * Everything here is DERIVED from the authored planar wall graph and is never
 * stored (docs/02 §6). Every function is pure: no clock, no randomness, no I/O,
 * no mutation of inputs. Two offline devices given the same plan must produce
 * byte-identical geometry with no coordination, so every iteration order in
 * this file is over SORTED ids rather than over input array order, and every
 * ranked choice is broken by a total order that ends in an id comparison.
 *
 * Four things live here.
 *
 *  1. MITER JOINTS. A wall is a centreline plus a thickness; the rendered
 *     polygon is the centreline offset to both sides and mitered against every
 *     other wall at each of its two nodes. The interesting part is entirely the
 *     degenerate cases: collinear continuations (the two offset lines are
 *     parallel and the miter point is at infinity), very acute wedges (the
 *     miter point shoots off and must be clamped to a bevel), walls of
 *     different thickness meeting at one node (the joint is a step, not a
 *     point), free ends (degree 1 -> a square cap), and 180-degree reversals.
 *
 *  2. OPENINGS. Doors and windows are hosted as (wallId, offset, width) and are
 *     cut out by 1-D INTERVAL SUBTRACTION in wall-local coordinates. There is no
 *     polygon-boolean library in this project and none is needed: the remaining
 *     solid pieces of a wall are the complement of the merged opening intervals
 *     in [0, length], and each piece is turned back into a quad by slicing the
 *     mitered polygon with two lines perpendicular to the centreline.
 *
 *  3. SNAPPING. A ranked engine over eight candidate kinds. Tolerance is
 *     expressed in WORLD MILLIMETRES derived from a screen-pixel tolerance and
 *     the current zoom, never as a fixed pixel constant — DPR varies per device
 *     and cannot be known before the tablet is measured (docs/02 §6).
 *
 *  4. TRANSFORMS. worldToScreen/screenToWorld (exact inverses), and two-point
 *     calibration for a traced background image. The calibration folds in a Y
 *     FLIP because image pixels are y-down and the world is y-up; forget it and
 *     the underlay renders mirrored and the calibrated rotation turns the wrong
 *     way (docs/02 §6).
 */

import type { FloorPlan, Opening, Wall } from '../entities/plan.js'
import type { NodeId, OpeningId, WallId } from '../ids.js'
import type { Deg, Mm, Vec2 } from '../units.js'
import {
  add, cross, deg, degToRad, dist, dot, len, mm, normalize, normRad, perp, rad, radToDeg, scale,
  sub, vec,
} from '../units.js'
import type { BuildGraphOptions, WallGraph } from './graph.js'
import {
  buildGraph, isSimplePolygon, MIN_OPENING_WIDTH_MM, MITER_LIMIT, openingInterval, PARALLEL_EPS,
  signedArea,
} from './graph.js'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Walls thinner than this are drawn at this thickness. A zero-thickness wall
 * has a zero-area polygon, which breaks every downstream invariant (winding,
 * simplicity, hit-testing) for a "wall" nobody can see anyway.
 */
export const MIN_WALL_THICKNESS_MM = 1

/**
 * A miter longer than this multiple of the half-thickness is a spike and is
 * replaced by a bevel. Shared with the room-offset solver in graph.ts so the
 * two agree about what counts as degenerate. 16 bevels wedges below ~7.2
 * degrees; SVG's default of 4 (below ~29 degrees) is far too eager for
 * architectural corners, which are routinely 30-45 degrees.
 */
export const DEFAULT_MITER_LIMIT = MITER_LIMIT

/**
 * A mitered polygon whose area falls below this fraction of length*thickness
 * has been eaten alive by its own joints; the plain butt rectangle is used
 * instead. Deep miters on a short wall are the case that triggers it.
 */
export const MIN_POLY_AREA_FACTOR = 0.25

/** Points closer than this (mm) are treated as the same polygon vertex. */
export const POLY_EPS_MM = 1e-9

/** Default angular step for angle snapping, in degrees (docs: multiples of 15). */
export const DEFAULT_ANGLE_STEP_DEG = 15

const cmpStr = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)
const cmpNum = (a: number, b: number): number => (a < b ? -1 : a > b ? 1 : 0)

/**
 * Fold negative zero onto positive zero.
 *
 * `-0` is arithmetically harmless but not STRUCTURALLY equal to `0`, and this
 * module produces values that end up AUTHORED on the plan and synced between
 * devices as one last-writer-wins record (docs/02 §6): calibration matrices,
 * and every world coordinate the snap engine hands back for a node the user is
 * about to place. `Math.round(-30 / 100) * 100` is `-0`, so a grid snap just
 * left of the origin lands on `-0` in the ordinary course of drawing. Two
 * identical plans would then compare as a change and round-trip differently
 * through a structural diff, so every such boundary canonicalises its zeros.
 */
const nz = (x: number): number => (x === 0 ? 0 : x)

/** A world point with its zeros canonicalised. See {@link nz}. */
const nzVec = (p: Vec2): Vec2 => vec(nz(p.x), nz(p.y))

/**
 * A caller-supplied numeric option, coerced back to `fallback` unless it is
 * finite and strictly positive.
 *
 * Every tunable in this file has a degenerate value that silently DISABLES a
 * safety net instead of throwing, which is the worst of both worlds. A NaN
 * `miterLimit` makes `ratio > miterLimit` false, so the spike clamp never
 * fires and the polygon area bound never trips. A NaN `minThicknessMm` makes
 * every half-thickness NaN, so every vertex is NaN. A NaN `maxChain` makes
 * `i < maxChain` false, so `snap` degrades to one non-idempotent pass. In each
 * case an invariant that is supposed to hold UNCONDITIONALLY quietly stops
 * holding, so none of these are taken on trust.
 */
const positiveOr = (v: number | undefined, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback

/** As {@link positiveOr}, but zero is a legitimate value. */
const nonNegativeOr = (v: number | undefined, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : fallback

// ---------------------------------------------------------------------------
// 1. MITER JOINTS
// ---------------------------------------------------------------------------

/**
 * What happened at one wedge of one node.
 *
 * - `cap`       degree-1 node: a free wall end, squared off across the wall.
 * - `miter`     the usual case: both walls share one corner point.
 * - `bevel`     the wedge was too acute (or two walls left the node in the same
 *               direction), so the miter was clamped and each wall got its own
 *               corner. The gap between them is the bevel edge.
 * - `collinear` the two walls continue straight through each other, so the
 *               offset lines are parallel and there is no intersection at all.
 *               With equal thicknesses the two corners coincide and the joint
 *               is invisible; with different thicknesses it is a step.
 */
export type JointKind = 'cap' | 'miter' | 'bevel' | 'collinear'

/**
 * One wedge between two angularly-adjacent walls at a node.
 *
 * The wedge is traversed counter-clockwise from `cwWallId` to `ccwWallId`, so
 * it is bounded by the LEFT side of `cwWallId` and the RIGHT side of
 * `ccwWallId` (left/right taken looking along each wall's direction AWAY from
 * the node). `cwPoint` is therefore the corner of `cwWallId`, and `ccwPoint`
 * the corner of `ccwWallId`. For `miter` they are the same point.
 */
export interface NodeJoint {
  readonly nodeId: NodeId
  readonly cwWallId: WallId
  readonly ccwWallId: WallId
  readonly kind: JointKind
  readonly cwPoint: Vec2
  readonly ccwPoint: Vec2
  /** |corner - node| / max half-thickness. Infinity when the miter is at infinity. */
  readonly miterRatio: number
  /** Angular size of the wedge, degrees, in [0, 360]. */
  readonly wedgeDeg: Deg
}

export interface WallPolygonOptions extends BuildGraphOptions {
  /** Miter longer than this multiple of the half-thickness bevels instead. */
  miterLimit?: number
  /**
   * How a free (degree-1) wall end is capped. `butt` (default) stops the wall
   * dead on its node; `square` extends it by half its thickness, matching the
   * stroke-linecap of the same name. Both ends are flat and perpendicular.
   */
  endCap?: 'butt' | 'square'
  /** Thickness floor, mm. Default MIN_WALL_THICKNESS_MM. */
  minThicknessMm?: number
}

export interface WallPolygon {
  readonly wallId: WallId
  /** Rendered outline, counter-clockwise in the y-up world. Always simple, always positive area. */
  readonly polygon: readonly Vec2[]
  /** Centreline endpoints, node A first. */
  readonly a: Vec2
  readonly b: Vec2
  /** Unit vector from a to b. */
  readonly direction: Vec2
  readonly length: Mm
  /** Thickness actually used, after the minimum-thickness floor. */
  readonly thickness: Mm
  readonly jointA: JointKind
  readonly jointB: JointKind
  /** True when the mitered outline failed validation and a butt rectangle was substituted. */
  readonly fallback: boolean
}

interface EdgeGeom {
  readonly index: number
  readonly wallId: WallId
  readonly origin: Vec2
  /** Unit direction, pointing AWAY from the node. */
  readonly d: Vec2
  /** perp(d): the left-hand normal in the y-up world. */
  readonly n: Vec2
  readonly half: number
}

interface ResolvedWallOptions {
  readonly minThickness: number
  readonly miterLimit: number
  /** 0 for a flush (butt) free end, 1 to extend it by half the thickness. */
  readonly capExtendFactor: number
}

/**
 * Sanitise the polygon options once, so every entry point agrees.
 *
 * `miterLimit` is clamped to at least 1 rather than merely checked for
 * finiteness: SVG defines a limit below 1 as invalid for the same reason it is
 * meaningless here — the miter ratio of a straight-through joint is already 1,
 * so no limit under 1 can describe anything a corner does.
 */
function resolveWallOptions(opts?: WallPolygonOptions): ResolvedWallOptions {
  const rawLimit = opts?.miterLimit
  return {
    minThickness: positiveOr(opts?.minThicknessMm, MIN_WALL_THICKNESS_MM),
    miterLimit:
      typeof rawLimit === 'number' && Number.isFinite(rawLimit)
        ? Math.max(1, rawLimit)
        : DEFAULT_MITER_LIMIT,
    capExtendFactor: opts?.endCap === 'square' ? 1 : 0,
  }
}

function effectiveThickness(w: Wall, minThickness: number): number {
  const t = Number.isFinite(w.thickness) ? Math.abs(w.thickness) : 0
  return Math.max(minThickness, t)
}

function edgeGeom(graph: WallGraph, index: number, minThickness: number): EdgeGeom | null {
  const he = graph.halfEdges[index]
  if (!he) return null
  const from = graph.nodes.get(he.from)
  const to = graph.nodes.get(he.to)
  const wall = graph.walls.get(he.wallId)
  if (!from || !to || !wall) return null
  const d = normalize(sub(to.p, from.p))
  if (d.x === 0 && d.y === 0) return null
  return {
    index,
    wallId: he.wallId,
    origin: from.p,
    d,
    n: perp(d),
    half: effectiveThickness(wall, minThickness) / 2,
  }
}

/** The point on `e`'s left offset line closest to the node (its square-cap corner). */
const leftFoot = (e: EdgeGeom, extend: number): Vec2 =>
  vec(e.origin.x + e.n.x * e.half - e.d.x * extend, e.origin.y + e.n.y * e.half - e.d.y * extend)

/** The point on `e`'s right offset line closest to the node. */
const rightFoot = (e: EdgeGeom, extend: number): Vec2 =>
  vec(e.origin.x - e.n.x * e.half - e.d.x * extend, e.origin.y - e.n.y * e.half - e.d.y * extend)

/**
 * Solve one wedge.
 *
 * Line 1 is the LEFT offset of `cw`:   dot(n1, X) = dot(n1, N) + h1
 * Line 2 is the RIGHT offset of `ccw`: dot(n2, X) = dot(n2, N) - h2
 *
 * The determinant of that 2x2 is cross(n1, n2) = cross(d1, d2), so it vanishes
 * exactly when the two walls are parallel — collinear continuation (dot < 0) or
 * two walls leaving the node in the same direction (dot > 0). Neither has an
 * intersection, so neither is divided through.
 */
function solveJoint(
  nodeId: NodeId,
  cw: EdgeGeom,
  ccw: EdgeGeom,
  wedgeDeg: Deg,
  miterLimit: number,
): NodeJoint {
  const det = cross(cw.d, ccw.d)

  if (Math.abs(det) < PARALLEL_EPS) {
    const straightThrough = dot(cw.d, ccw.d) < 0
    // Collinear continuation: both corners sit on the same side of the shared
    // centreline, at each wall's own half-thickness. Equal thicknesses put them
    // on top of each other; different thicknesses leave an honest step.
    // Same-direction walls (a zero-width wedge) get squared off instead.
    return {
      nodeId,
      cwWallId: cw.wallId,
      ccwWallId: ccw.wallId,
      kind: straightThrough ? 'collinear' : 'bevel',
      cwPoint: leftFoot(cw, 0),
      ccwPoint: rightFoot(ccw, 0),
      miterRatio: Number.POSITIVE_INFINITY,
      wedgeDeg,
    }
  }

  const c1 = dot(cw.n, cw.origin) + cw.half
  const c2 = dot(ccw.n, ccw.origin) - ccw.half
  const x = (c1 * ccw.n.y - cw.n.y * c2) / det
  const y = (cw.n.x * c2 - c1 * ccw.n.x) / det
  const p = vec(x, y)

  const maxHalf = Math.max(cw.half, ccw.half)
  const ratio = maxHalf > 0 ? dist(p, cw.origin) / maxHalf : 0

  if (!Number.isFinite(x) || !Number.isFinite(y) || ratio > miterLimit) {
    // Spike. Clamp to a bevel: each wall keeps its own square corner and the
    // edge between them closes the joint.
    return {
      nodeId,
      cwWallId: cw.wallId,
      ccwWallId: ccw.wallId,
      kind: 'bevel',
      cwPoint: leftFoot(cw, 0),
      ccwPoint: rightFoot(ccw, 0),
      miterRatio: ratio,
      wedgeDeg,
    }
  }

  return {
    nodeId,
    cwWallId: cw.wallId,
    ccwWallId: ccw.wallId,
    kind: 'miter',
    cwPoint: p,
    ccwPoint: p,
    miterRatio: ratio,
    wedgeDeg,
  }
}

/**
 * The angular size of each wedge, in the same order as `edges`.
 *
 * `edges` arrives sorted by angle ascending (the graph's embedding order), so
 * each wedge is the plain forward difference and the LAST one closes the circle.
 * Computing every wedge as a normalised difference instead is wrong in exactly
 * one case, and a property test found it: when two walls leave the node in the
 * SAME direction, the wrap-around wedge is 360 degrees, and normalising folds
 * that to 0 — leaving a node whose wedges sum to 0 instead of 360.
 */
function wedgeAngles(edges: readonly EdgeGeom[]): Deg[] {
  const k = edges.length
  const angles = edges.map(e => radToDeg(rad(Math.atan2(e.d.y, e.d.x))))
  return angles.map((a, i) =>
    i < k - 1 ? deg((angles[i + 1] ?? a) - a) : deg(360 - (a - (angles[0] ?? a))),
  )
}

/**
 * Every joint at one node, in counter-clockwise wedge order.
 *
 * Degree 0 -> no joints. Degree 1 -> exactly one `cap` joint whose two points
 * are the two corners of the squared-off free end. Degree n>=2 -> n joints, one
 * per wedge between angularly adjacent walls.
 */
export function nodeJoints(
  graph: WallGraph,
  nodeId: NodeId,
  opts?: WallPolygonOptions,
): NodeJoint[] {
  const canonical = graph.canonical.get(nodeId) ?? nodeId
  const node = graph.nodes.get(canonical)
  if (!node) return []
  const { minThickness, miterLimit, capExtendFactor } = resolveWallOptions(opts)

  const edges: EdgeGeom[] = []
  for (const idx of node.outgoing) {
    const e = edgeGeom(graph, idx, minThickness)
    if (e) edges.push(e)
  }
  if (edges.length === 0) return []

  if (edges.length === 1) {
    const e = edges[0]!
    const extend = e.half * capExtendFactor
    return [{
      nodeId: canonical,
      cwWallId: e.wallId,
      ccwWallId: e.wallId,
      kind: 'cap',
      cwPoint: leftFoot(e, extend),
      ccwPoint: rightFoot(e, extend),
      miterRatio: 0,
      wedgeDeg: deg(360),
    }]
  }

  const wedges = wedgeAngles(edges)
  const joints: NodeJoint[] = []
  for (let i = 0; i < edges.length; i++) {
    const cw = edges[i]!
    const ccw = edges[(i + 1) % edges.length]!
    joints.push(solveJoint(canonical, cw, ccw, wedges[i] ?? deg(0), miterLimit))
  }
  return joints
}

/** How degenerate each joint kind is. A wall end reports its worst joint. */
const JOINT_RANK: Readonly<Record<JointKind, number>> = Object.freeze({
  miter: 0, collinear: 1, bevel: 2, cap: 3,
})

/** Corner lookup: half-edge index -> its left / right corner point. */
interface CornerTable {
  readonly left: ReadonlyMap<number, Vec2>
  readonly right: ReadonlyMap<number, Vec2>
  readonly kindOf: ReadonlyMap<number, JointKind>
}

function buildCorners(graph: WallGraph, opts?: WallPolygonOptions): CornerTable {
  const left = new Map<number, Vec2>()
  const right = new Map<number, Vec2>()
  const kindOf = new Map<number, JointKind>()
  const { minThickness, miterLimit, capExtendFactor } = resolveWallOptions(opts)

  for (const nodeId of graph.nodeIds) {
    const node = graph.nodes.get(nodeId)!
    const edges: EdgeGeom[] = []
    for (const idx of node.outgoing) {
      const e = edgeGeom(graph, idx, minThickness)
      if (e) edges.push(e)
    }
    if (edges.length === 0) continue

    if (edges.length === 1) {
      const e = edges[0]!
      const extend = e.half * capExtendFactor
      left.set(e.index, leftFoot(e, extend))
      right.set(e.index, rightFoot(e, extend))
      kindOf.set(e.index, 'cap')
      continue
    }

    const wedges = wedgeAngles(edges)
    for (let i = 0; i < edges.length; i++) {
      const cw = edges[i]!
      const ccw = edges[(i + 1) % edges.length]!
      const j = solveJoint(nodeId, cw, ccw, wedges[i] ?? deg(0), miterLimit)
      // The wedge supplies the LEFT corner of the clockwise wall and the RIGHT
      // corner of the counter-clockwise one.
      left.set(cw.index, j.cwPoint)
      right.set(ccw.index, j.ccwPoint)
      // A wall's end is reported by whichever of its two joints is the more
      // degenerate; 'miter' is the boring case and loses.
      for (const idx of [cw.index, ccw.index]) {
        const prev = kindOf.get(idx)
        if (prev === undefined || JOINT_RANK[j.kind] > JOINT_RANK[prev]) kindOf.set(idx, j.kind)
      }
    }
  }
  return { left, right, kindOf }
}

/** Drop consecutive duplicate vertices (including the wrap-around pair). */
function dedupeRing(ring: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = []
  for (const p of ring) {
    const last = out[out.length - 1]
    if (last && Math.abs(last.x - p.x) <= POLY_EPS_MM && Math.abs(last.y - p.y) <= POLY_EPS_MM) {
      continue
    }
    out.push(p)
  }
  while (out.length > 1) {
    const first = out[0]!
    const last = out[out.length - 1]!
    if (Math.abs(first.x - last.x) <= POLY_EPS_MM && Math.abs(first.y - last.y) <= POLY_EPS_MM) {
      out.pop()
    } else break
  }
  return out
}

/** The plain butt rectangle for a centreline: always simple, always CCW, area exactly L*T. */
export function buttRectangle(a: Vec2, b: Vec2, thickness: number): Vec2[] {
  const d = normalize(sub(b, a))
  if (d.x === 0 && d.y === 0) return []
  const n = perp(d)
  const h = Math.max(0, thickness) / 2
  return [
    vec(a.x - n.x * h, a.y - n.y * h),
    vec(b.x - n.x * h, b.y - n.y * h),
    vec(b.x + n.x * h, b.y + n.y * h),
    vec(a.x + n.x * h, a.y + n.y * h),
  ]
}

/**
 * Accept a candidate outline only if it is a genuine, sane wall body; otherwise
 * substitute the butt rectangle.
 *
 * This is what makes the property "every wall polygon is simple, positively
 * oriented and within a sane factor of length*thickness" true by construction
 * rather than by hope. The miter solver is correct for well-conditioned input
 * and this is the net under it.
 */
function finalisePolygon(
  candidate: readonly Vec2[],
  a: Vec2,
  b: Vec2,
  thickness: number,
  miterLimit: number,
  minAreaFactor: number = MIN_POLY_AREA_FACTOR,
): { polygon: Vec2[]; fallback: boolean } {
  const fallback = buttRectangle(a, b, thickness)
  const ring = dedupeRing(candidate)
  if (ring.length < 3) return { polygon: fallback, fallback: true }
  for (const p of ring) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return { polygon: fallback, fallback: true }
  }
  const area = signedArea(ring)
  const L = dist(a, b)
  const nominal = L * thickness
  if (!(area > 0)) return { polygon: fallback, fallback: true }
  if (area < minAreaFactor * nominal) return { polygon: fallback, fallback: true }
  if (area > (L + 2 * miterLimit * thickness) * thickness) return { polygon: fallback, fallback: true }
  if (!isSimplePolygon(ring)) return { polygon: fallback, fallback: true }
  return { polygon: ring, fallback: false }
}

/**
 * The rendered outline of every wall in the plan, mitered at every node.
 *
 * Ordered by wall id so the result never depends on the order of `plan.walls`.
 * Walls the graph rejected (missing node, zero length, duplicate) are absent.
 */
export function wallPolygons(plan: FloorPlan, opts?: WallPolygonOptions): WallPolygon[] {
  const graph = buildGraph(plan, opts)
  return wallPolygonsFromGraph(graph, opts)
}

/** As {@link wallPolygons}, for a graph that has already been built. */
export function wallPolygonsFromGraph(
  graph: WallGraph,
  opts?: WallPolygonOptions,
): WallPolygon[] {
  const { minThickness, miterLimit } = resolveWallOptions(opts)
  const corners = buildCorners(graph, opts)
  const out: WallPolygon[] = []

  for (const wallId of graph.wallIds) {
    const wall = graph.walls.get(wallId)
    const pair = graph.wallEdges.get(wallId)
    if (!wall || !pair) continue
    const nodeA = graph.nodes.get(wall.a)
    const nodeB = graph.nodes.get(wall.b)
    if (!nodeA || !nodeB) continue
    const a = nodeA.p
    const b = nodeB.p
    const L = dist(a, b)
    if (!(L > 0)) continue
    const t = effectiveThickness(wall, minThickness)
    const [hAB, hBA] = pair

    // hBA travels b -> a, so ITS left side is the wall's right side and vice
    // versa. Getting this backwards is the classic way to produce a bow-tie.
    const rA = corners.right.get(hAB)
    const lA = corners.left.get(hAB)
    const rB = corners.left.get(hBA)
    const lB = corners.right.get(hBA)
    const candidate = rA && rB && lB && lA ? [rA, rB, lB, lA] : []

    const { polygon, fallback } = finalisePolygon(candidate, a, b, t, miterLimit)
    out.push({
      wallId,
      polygon,
      a,
      b,
      direction: normalize(sub(b, a)),
      length: mm(L),
      thickness: mm(t),
      jointA: corners.kindOf.get(hAB) ?? 'cap',
      jointB: corners.kindOf.get(hBA) ?? 'cap',
      fallback,
    })
  }
  return out
}

// ---------------------------------------------------------------------------
// 2. OPENINGS — 1-D interval subtraction in wall-local coordinates
// ---------------------------------------------------------------------------

/** A solid piece of wall, in wall-local coordinates measured from node A. */
export interface WallSegment {
  readonly start: Mm
  readonly end: Mm
  readonly length: Mm
}

/** A merged run of openings. Several overlapping doors collapse into one cut. */
export interface OpeningCut {
  readonly start: Mm
  readonly end: Mm
  /** Ids of every opening that contributed, ascending. */
  readonly openingIds: readonly OpeningId[]
}

export type OpeningDropReason =
  | 'non-positive-width'
  | 'wrong-wall'
  | 'out-of-range'
  | 'clamped-away'

export interface DroppedOpening {
  readonly openingId: OpeningId
  readonly reason: OpeningDropReason
}

export interface WallOpeningLayout {
  readonly length: Mm
  readonly segments: readonly WallSegment[]
  readonly cuts: readonly OpeningCut[]
  readonly dropped: readonly DroppedOpening[]
}

export interface OpeningLayoutOptions {
  /** Solid pieces at or below this length (mm) are discarded. Default 0. */
  minSegmentMm?: number
  /** Openings narrower than this after clamping are discarded. Default 1 mm. */
  minOpeningMm?: number
  /**
   * The wall these openings are supposed to be hosted on.
   *
   * Openings are hosted as `(wallId, offset, width)` (docs/02 §6), but the
   * interval subtraction itself only needs a length and a list of intervals —
   * so nothing stops a caller handing over `plan.openings` wholesale and
   * silently cutting one wall's doors out of another. Set this and every
   * foreign opening is reported in `dropped` with reason `'wrong-wall'`
   * instead. {@link planWallLayout} and {@link wallPieces} always set it.
   */
  hostWallId?: WallId
}

const segment = (start: number, end: number): WallSegment => ({
  start: mm(start),
  end: mm(end),
  length: mm(end - start),
})

/**
 * Solid pieces of a wall after its openings are cut out, plus the merged cuts
 * and a reason for every opening that was discarded.
 *
 * The whole of the opening story is here, and it is one-dimensional:
 *
 *  - An opening covers [offset, offset + width] measured from node A. (That
 *    reading of `Opening.offset` comes from `openingInterval` in graph.ts, so
 *    the two modules cannot drift apart.)
 *  - Openings are CLAMPED to [0, length], never dropped for merely hanging over
 *    an edge — a door that lost 30 mm when its wall was shortened is still a
 *    door, and silently deleting the user's doors is much worse than trimming
 *    one. Only what survives with less than `minOpeningMm` is discarded.
 *  - Overlapping and touching openings MERGE, so a double door entered as two
 *    overlapping leaves does not leave a hairline pier between them.
 *  - The result is the complement of the merged cuts in [0, length]: disjoint,
 *    sorted, and (with the default `minSegmentMm` of 0) exactly covering the
 *    wall together with the cuts.
 */
export function wallOpeningLayout(
  wallLength: Mm | number,
  openings: readonly Opening[],
  opts?: OpeningLayoutOptions,
): WallOpeningLayout {
  const minSegment = nonNegativeOr(opts?.minSegmentMm, 0)
  const minOpening = nonNegativeOr(opts?.minOpeningMm, MIN_OPENING_WIDTH_MM)
  const host = opts?.hostWallId
  const L = Number.isFinite(wallLength) ? Math.max(0, wallLength) : 0
  const dropped: DroppedOpening[] = []
  const sorted = [...openings].sort((x, y) => cmpStr(x.id, y.id))
  const foreign = (o: Opening): boolean => host !== undefined && o.wallId !== host

  if (!(L > 0)) {
    for (const o of sorted) {
      dropped.push({ openingId: o.id, reason: foreign(o) ? 'wrong-wall' : 'out-of-range' })
    }
    return { length: mm(0), segments: [], cuts: [], dropped }
  }

  interface Kept { start: number; end: number; id: OpeningId }
  const kept: Kept[] = []
  for (const o of sorted) {
    if (foreign(o)) {
      dropped.push({ openingId: o.id, reason: 'wrong-wall' })
      continue
    }
    const raw = openingInterval(o)
    if (!Number.isFinite(raw.start) || !Number.isFinite(raw.end) || !(raw.end > raw.start)) {
      dropped.push({ openingId: o.id, reason: 'non-positive-width' })
      continue
    }
    const start = Math.max(0, raw.start)
    const end = Math.min(L, raw.end)
    if (!(end > start)) {
      dropped.push({ openingId: o.id, reason: 'out-of-range' })
      continue
    }
    if (end - start < minOpening) {
      dropped.push({ openingId: o.id, reason: 'clamped-away' })
      continue
    }
    kept.push({ start, end, id: o.id })
  }

  kept.sort((x, y) => cmpNum(x.start, y.start) || cmpNum(x.end, y.end) || cmpStr(x.id, y.id))

  const cuts: OpeningCut[] = []
  let curStart = 0
  let curEnd = 0
  let curIds: OpeningId[] = []
  for (const k of kept) {
    if (curIds.length === 0) {
      curStart = k.start
      curEnd = k.end
      curIds = [k.id]
      continue
    }
    if (k.start <= curEnd) {
      curEnd = Math.max(curEnd, k.end)
      curIds.push(k.id)
    } else {
      cuts.push({ start: mm(curStart), end: mm(curEnd), openingIds: [...curIds].sort(cmpStr) })
      curStart = k.start
      curEnd = k.end
      curIds = [k.id]
    }
  }
  if (curIds.length > 0) {
    cuts.push({ start: mm(curStart), end: mm(curEnd), openingIds: [...curIds].sort(cmpStr) })
  }

  const segments: WallSegment[] = []
  let cursor = 0
  for (const c of cuts) {
    if (c.start - cursor > minSegment) segments.push(segment(cursor, c.start))
    cursor = Math.max(cursor, c.end)
  }
  if (L - cursor > minSegment) segments.push(segment(cursor, L))

  return { length: mm(L), segments, cuts, dropped }
}

/**
 * The remaining solid intervals along a wall of the given length.
 *
 * SPEC NOTE: the brief writes this as `wallSegmentsAfterOpenings(wall, openings)`,
 * but `Wall` carries node ids, not a length — the length only exists once the
 * node positions are known. The length is therefore passed explicitly, and
 * {@link planWallSegments} is the convenience that resolves it from a plan.
 */
export function wallSegmentsAfterOpenings(
  wallLength: Mm | number,
  openings: readonly Opening[],
  opts?: OpeningLayoutOptions,
): WallSegment[] {
  return [...wallOpeningLayout(wallLength, openings, opts).segments]
}

/**
 * Node positions, with duplicate ids resolved to the lexicographically smallest
 * position rather than to whichever came first in the array — the same rule
 * `buildGraph` uses, and for the same reason: array order must not change the
 * answer.
 */
export function nodePositions(plan: FloorPlan): Map<NodeId, Vec2> {
  const pos = new Map<NodeId, Vec2>()
  for (const n of plan.nodes) {
    const prev = pos.get(n.id)
    if (prev === undefined || n.p.x < prev.x || (n.p.x === prev.x && n.p.y < prev.y)) {
      pos.set(n.id, n.p)
    }
  }
  return pos
}

/** Centreline length of one wall in a plan, 0 if it or either node is missing. */
export function wallCentrelineLength(plan: FloorPlan, wallId: WallId): Mm {
  const wall = plan.walls.find(w => w.id === wallId)
  if (!wall) return mm(0)
  const pos = nodePositions(plan)
  const a = pos.get(wall.a)
  const b = pos.get(wall.b)
  if (!a || !b) return mm(0)
  return mm(dist(a, b))
}

/** Openings hosted on one wall, ascending by id. */
export function openingsOnWall(plan: FloorPlan, wallId: WallId): Opening[] {
  return plan.openings.filter(o => o.wallId === wallId).sort((a, b) => cmpStr(a.id, b.id))
}

/** {@link wallOpeningLayout} for a wall resolved from the plan. */
export function planWallLayout(
  plan: FloorPlan,
  wallId: WallId,
  opts?: OpeningLayoutOptions,
): WallOpeningLayout {
  return wallOpeningLayout(
    wallCentrelineLength(plan, wallId),
    openingsOnWall(plan, wallId),
    { ...opts, hostWallId: wallId },
  )
}

/** {@link wallSegmentsAfterOpenings} for a wall resolved from the plan. */
export function planWallSegments(
  plan: FloorPlan,
  wallId: WallId,
  opts?: OpeningLayoutOptions,
): WallSegment[] {
  return [...planWallLayout(plan, wallId, opts).segments]
}

/** One drawable piece of a wall: the mitered body sliced at the openings. */
export interface WallPiece {
  readonly wallId: WallId
  readonly segment: WallSegment
  readonly polygon: readonly Vec2[]
  readonly fallback: boolean
}

export type WallPieceOptions = WallPolygonOptions & OpeningLayoutOptions

/**
 * The wall bodies actually drawn: every wall's mitered polygon, sliced by its
 * openings.
 *
 * The slice is trivial because both long sides of the polygon lie ON the wall's
 * two offset lines, so a cut at wall-local coordinate s is just the pair of
 * points at parameter s on those two lines. Only the pieces that reach the ends
 * of the wall keep the mitered corners; interior cuts are square. No polygon
 * boolean anywhere (docs/02 §6).
 *
 * The one subtlety is that THE TWO FLANKS DO NOT SHARE A PARAMETER RANGE. A
 * miter pulls one flank back by exactly as much as it pushes the other forward,
 * so at an L-corner one flank of a 1000 mm wall runs to 1100 and the other stops
 * at 900. A cut placed at 950 is inside the wall on one flank and past the end
 * of it on the other, and taking it literally produces a bow-tie — which the
 * validator then replaces with a full-width rectangle that pokes out through the
 * joint into the neighbouring wall. So each cut is clamped to its own flank's
 * extent, which turns that piece into the honest corner triangle instead and
 * keeps every piece inside the body it was cut from.
 */
export function wallPieces(plan: FloorPlan, opts?: WallPieceOptions): WallPiece[] {
  const polys = wallPolygons(plan, opts)
  const byWall = new Map<WallId, Opening[]>()
  for (const o of [...plan.openings].sort((a, b) => cmpStr(a.id, b.id))) {
    const bucket = byWall.get(o.wallId)
    if (bucket) bucket.push(o)
    else byWall.set(o.wallId, [o])
  }

  const { miterLimit } = resolveWallOptions(opts)
  const out: WallPiece[] = []
  for (const wp of polys) {
    const layout = wallOpeningLayout(wp.length, byWall.get(wp.wallId) ?? [], {
      ...(opts?.minSegmentMm !== undefined ? { minSegmentMm: opts.minSegmentMm } : {}),
      ...(opts?.minOpeningMm !== undefined ? { minOpeningMm: opts.minOpeningMm } : {}),
      hostWallId: wp.wallId,
    })
    const L = wp.length
    const d = wp.direction
    const n = perp(d)
    const h = wp.thickness / 2
    const onRight = (s: number): Vec2 =>
      vec(wp.a.x + d.x * s - n.x * h, wp.a.y + d.y * s - n.y * h)
    const onLeft = (s: number): Vec2 =>
      vec(wp.a.x + d.x * s + n.x * h, wp.a.y + d.y * s + n.y * h)

    // The mitered corners, in polygon order [rA, rB, lB, lA].
    const ring = wp.polygon
    const corners = ring.length === 4
      ? { rA: ring[0]!, rB: ring[1]!, lB: ring[2]!, lA: ring[3]! }
      : null

    // Where each flank actually starts and ends, in wall-local parameters.
    const param = (p: Vec2): number => dot(sub(p, wp.a), d)
    const rMin = corners ? param(corners.rA) : 0
    const rMax = corners ? param(corners.rB) : L
    const lMin = corners ? param(corners.lA) : 0
    const lMax = corners ? param(corners.lB) : L
    const clamp = (s: number, lo: number, hi: number): number =>
      Math.min(Math.max(s, Math.min(lo, hi)), Math.max(lo, hi))

    for (const seg of layout.segments) {
      const atStart = seg.start <= POLY_EPS_MM
      const atEnd = seg.end >= L - POLY_EPS_MM
      const rs0 = clamp(seg.start, rMin, rMax)
      const rs1 = clamp(seg.end, rMin, rMax)
      const ls0 = clamp(seg.start, lMin, lMax)
      const ls1 = clamp(seg.end, lMin, lMax)
      // Both flanks collapsed: this solid interval lies entirely past the end of
      // the mitered body and there is nothing of it left to draw.
      if (rs1 - rs0 <= POLY_EPS_MM && ls1 - ls0 <= POLY_EPS_MM) continue
      const rStart = atStart && corners ? corners.rA : onRight(rs0)
      const lStart = atStart && corners ? corners.lA : onLeft(ls0)
      const rEnd = atEnd && corners ? corners.rB : onRight(rs1)
      const lEnd = atEnd && corners ? corners.lB : onLeft(ls1)
      const a = add(wp.a, scale(d, seg.start))
      const b = add(wp.a, scale(d, seg.end))
      // Minimum area factor 0, unlike the parent wall. A wall whose own joints
      // have eaten it down to a quarter of length*thickness is degenerate and
      // wants the plain rectangle; a PIECE that small is just the corner sliver
      // left over next to a deep miter, and it is the correct answer. Applying
      // the parent's floor here would swap that sliver for a full-width
      // rectangle that overhangs the joint — the opposite of the intent.
      const { polygon, fallback } = finalisePolygon(
        [rStart, rEnd, lEnd, lStart], a, b, wp.thickness, miterLimit, 0,
      )
      out.push({ wallId: wp.wallId, segment: seg, polygon, fallback })
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// 3. SNAPPING
// ---------------------------------------------------------------------------

/**
 * Candidate kinds, in priority order. Earlier beats later, always, regardless
 * of distance: an endpoint 9 mm away wins over a grid point 0.2 mm away,
 * because the user who is within tolerance of a corner meant the corner.
 */
export const SNAP_KINDS = [
  'endpoint',
  'midpoint',
  'intersection',
  'perpendicular',
  'extension',
  'parallel',
  'grid',
  'angle',
] as const

export type SnapKind = (typeof SNAP_KINDS)[number]

/** Lower number = higher priority. */
export const SNAP_PRIORITY: Readonly<Record<SnapKind, number>> = Object.freeze(
  Object.fromEntries(SNAP_KINDS.map((k, i) => [k, i])) as Record<SnapKind, number>,
)

export interface SnapResult {
  readonly point: Vec2
  readonly kind: SnapKind
  /** Node id, wall id, or `a~b` for an intersection. Null for grid and angle. */
  readonly targetId: string | null
  /** Distance from the ORIGINAL query point to the returned point, in mm. */
  readonly distance: Mm
}

/** What the tablet's glass actually is, measured on the device (docs/02 §1, §6). */
export interface DeviceMetrics {
  /** Device (physical) pixels per CSS pixel — `window.devicePixelRatio`. */
  readonly devicePixelRatio: number
  /** Device pixels per physical millimetre of glass. Measured, never assumed. */
  readonly devicePxPerMm: number
}

export interface SnapOptions {
  /**
   * Tolerance in CSS pixels. Derive it from a physical size with
   * {@link fingerToleranceCssPx} — a hardcoded pixel constant is wrong on any
   * device whose DPR differs from the one it was tuned on (docs/02 §6).
   */
  readonly screenTolerancePx: number
  /** CSS pixels per world millimetre. The current zoom. */
  readonly zoom: number
  /** Grid pitch in mm for grid snapping. Defaults to `plan.gridSize`. */
  readonly gridMm?: number
  /** Anchor of the segment being drawn. Perpendicular/parallel/angle need it. */
  readonly origin?: Vec2 | null
  /** Angular step in degrees for angle snapping. Default 15. */
  readonly angleStepDeg?: number
  /** Restrict angle snapping to the four axis directions. */
  readonly orthoLock?: boolean
  /** Enabled candidate kinds. Default: all of them. */
  readonly kinds?: readonly SnapKind[]
  /** Walls to ignore — typically the one being dragged. */
  readonly excludeWallIds?: readonly WallId[]
  /** Nodes to ignore. */
  readonly excludeNodeIds?: readonly NodeId[]
  /** Maximum re-snap hops when settling to a fixed point. Default SNAP_KINDS.length + 1. */
  readonly maxChain?: number
}

/**
 * Screen-pixel tolerance for a physical target size.
 *
 * A finger lands with about 4 mm of systematic error at the glass (docs/02 §6),
 * so snap radii are authored in physical millimetres and converted here. Both
 * factors matter: `devicePxPerMm` says how big a device pixel is, and
 * `devicePixelRatio` converts device pixels to the CSS pixels the canvas is
 * addressed in.
 */
export function fingerToleranceCssPx(fingerMm: number, device: DeviceMetrics): number {
  const dpr = device.devicePixelRatio
  if (!(dpr > 0) || !Number.isFinite(dpr)) return 0
  if (!Number.isFinite(device.devicePxPerMm) || !(device.devicePxPerMm > 0)) return 0
  if (!Number.isFinite(fingerMm) || fingerMm <= 0) return 0
  return (fingerMm * device.devicePxPerMm) / dpr
}

/** World-millimetre tolerance for a screen-pixel tolerance at a given zoom. */
export function worldTolerance(screenTolerancePx: number, zoom: number): Mm {
  if (!Number.isFinite(screenTolerancePx) || screenTolerancePx < 0) return mm(0)
  if (!Number.isFinite(zoom) || zoom <= 0) return mm(0)
  return mm(screenTolerancePx / zoom)
}

/**
 * Constrain a drawn direction to multiples of `stepDeg` (default 15), or to the
 * four axes under ortho lock.
 *
 * The point is PROJECTED onto the chosen ray rather than rotated onto it, which
 * is what every CAD ortho lock does: the cursor's along-axis distance survives
 * and only the sideways component is discarded. The nearest allowed direction is
 * never more than half a step away, so the projection length is never negative.
 */
export function constrainAngle(
  origin: Vec2,
  point: Vec2,
  opts?: { stepDeg?: number; ortho?: boolean },
): Vec2 {
  const v = sub(point, origin)
  const r = len(v)
  if (r === 0) return vec(origin.x, origin.y)
  const rawStep = opts?.ortho ? 90 : (opts?.stepDeg ?? DEFAULT_ANGLE_STEP_DEG)
  const step = Number.isFinite(rawStep) && rawStep > 0 ? rawStep : DEFAULT_ANGLE_STEP_DEG
  const angle = radToDeg(rad(Math.atan2(v.y, v.x)))
  const snapped = Math.round(angle / step) * step
  const theta = degToRad(deg(snapped))
  const u = vec(Math.cos(theta), Math.sin(theta))
  const t = dot(v, u)
  return nzVec(vec(origin.x + u.x * t, origin.y + u.y * t))
}

interface RawCandidate {
  readonly kind: SnapKind
  readonly point: Vec2
  readonly targetId: string | null
}

interface ResolvedWall {
  readonly id: WallId
  readonly a: Vec2
  readonly b: Vec2
  readonly d: Vec2
  readonly length: number
  readonly nodeA: NodeId
  readonly nodeB: NodeId
}

function resolveWalls(plan: FloorPlan, exclude: ReadonlySet<WallId>): ResolvedWall[] {
  const pos = nodePositions(plan)
  const out: ResolvedWall[] = []
  for (const w of [...plan.walls].sort((x, y) => cmpStr(x.id, y.id))) {
    if (exclude.has(w.id)) continue
    const a = pos.get(w.a)
    const b = pos.get(w.b)
    if (!a || !b) continue
    const L = dist(a, b)
    if (!(L > 0)) continue
    out.push({ id: w.id, a, b, d: normalize(sub(b, a)), length: L, nodeA: w.a, nodeB: w.b })
  }
  return out
}

/** Signed position of p along the wall, measured from a, in mm. */
const along = (w: ResolvedWall, p: Vec2): number => dot(sub(p, w.a), w.d)

/** Point at wall-local coordinate s on the wall's infinite line. */
const atAlong = (w: ResolvedWall, s: number): Vec2 =>
  vec(w.a.x + w.d.x * s, w.a.y + w.d.y * s)

/**
 * Every candidate of every enabled kind, unfiltered by distance.
 *
 * Deliberately built from SORTED ids: the candidate multiset must not depend on
 * the order of `plan.nodes` or `plan.walls`, or two devices holding the same
 * plan in different insertion orders would snap differently.
 */
function generateCandidates(point: Vec2, plan: FloorPlan, opts: SnapOptions): RawCandidate[] {
  const enabled = new Set<SnapKind>(opts.kinds ?? SNAP_KINDS)
  const excludeWalls = new Set<WallId>(opts.excludeWallIds ?? [])
  const excludeNodes = new Set<NodeId>(opts.excludeNodeIds ?? [])
  const origin = opts.origin ?? null
  const walls = resolveWalls(plan, excludeWalls)
  const out: RawCandidate[] = []

  if (enabled.has('endpoint')) {
    const pos = nodePositions(plan)
    for (const id of [...pos.keys()].sort(cmpStr)) {
      if (excludeNodes.has(id)) continue
      out.push({ kind: 'endpoint', point: pos.get(id)!, targetId: id })
    }
  }

  if (enabled.has('midpoint')) {
    for (const w of walls) {
      out.push({ kind: 'midpoint', point: atAlong(w, w.length / 2), targetId: w.id })
    }
  }

  if (enabled.has('intersection')) {
    for (let i = 0; i < walls.length; i++) {
      for (let j = i + 1; j < walls.length; j++) {
        const w1 = walls[i]!
        const w2 = walls[j]!
        // Walls that share a node already intersect at an endpoint candidate.
        if (w1.nodeA === w2.nodeA || w1.nodeA === w2.nodeB) continue
        if (w1.nodeB === w2.nodeA || w1.nodeB === w2.nodeB) continue
        const det = cross(w1.d, w2.d)
        if (Math.abs(det) < PARALLEL_EPS) continue
        const rel = sub(w2.a, w1.a)
        const t = cross(rel, w2.d) / det
        const u = cross(rel, w1.d) / det
        if (t < 0 || t > w1.length || u < 0 || u > w2.length) continue
        const key = cmpStr(w1.id, w2.id) <= 0 ? `${w1.id}~${w2.id}` : `${w2.id}~${w1.id}`
        out.push({ kind: 'intersection', point: atAlong(w1, t), targetId: key })
      }
    }
  }

  if (origin && enabled.has('perpendicular')) {
    for (const w of walls) {
      const s = along(w, origin)
      if (s < 0 || s > w.length) continue
      out.push({ kind: 'perpendicular', point: atAlong(w, s), targetId: w.id })
    }
  }

  if (enabled.has('extension')) {
    for (const w of walls) {
      const s = along(w, point)
      // Strictly beyond an endpoint, by a clear margin. The margin is not
      // cosmetic: a candidate generated at s = length + 1 ulp would vanish when
      // the engine re-queried from the point it just produced, and the fixed
      // point that makes `snap` idempotent would not exist.
      if (s >= -POLY_EPS_MM && s <= w.length + POLY_EPS_MM) continue
      out.push({ kind: 'extension', point: atAlong(w, s), targetId: w.id })
    }
  }

  if (origin && enabled.has('parallel')) {
    for (const w of walls) {
      const t = dot(sub(point, origin), w.d)
      out.push({
        kind: 'parallel',
        point: vec(origin.x + w.d.x * t, origin.y + w.d.y * t),
        targetId: w.id,
      })
    }
  }

  if (enabled.has('grid')) {
    const g = opts.gridMm ?? plan.gridSize
    if (Number.isFinite(g) && g > 0) {
      // The grid is where a negative zero is actually reachable: a cursor a few
      // millimetres left of an axis rounds to -0, and that -0 would travel
      // straight into an authored node coordinate. The results are canonicalised
      // again on the way out, so this is belt and braces. See {@link nz}.
      out.push({
        kind: 'grid',
        point: nzVec(vec(Math.round(point.x / g) * g, Math.round(point.y / g) * g)),
        targetId: null,
      })
    }
  }

  if (origin && enabled.has('angle')) {
    const angleOpts: { stepDeg?: number; ortho?: boolean } = {}
    if (opts.angleStepDeg !== undefined) angleOpts.stepDeg = opts.angleStepDeg
    if (opts.orthoLock !== undefined) angleOpts.ortho = opts.orthoLock
    out.push({ kind: 'angle', point: constrainAngle(origin, point, angleOpts), targetId: null })
  }

  return out
}

/**
 * Total order over candidates: priority, then distance, then id, then position.
 *
 * The tail of that order is what makes the engine order-independent. Two walls
 * whose midpoints are equidistant from the cursor would otherwise resolve to
 * whichever happened to be earlier in the array.
 */
function compareCandidates(
  a: { kind: SnapKind; distance: number; targetId: string | null; point: Vec2 },
  b: { kind: SnapKind; distance: number; targetId: string | null; point: Vec2 },
): number {
  return (
    cmpNum(SNAP_PRIORITY[a.kind], SNAP_PRIORITY[b.kind]) ||
    cmpNum(a.distance, b.distance) ||
    cmpStr(a.targetId ?? '', b.targetId ?? '') ||
    cmpNum(a.point.x, b.point.x) ||
    cmpNum(a.point.y, b.point.y)
  )
}

/** Every candidate within tolerance, best first. */
export function snapCandidates(
  point: Vec2,
  plan: FloorPlan,
  opts: SnapOptions,
): SnapResult[] {
  const tol = worldTolerance(opts.screenTolerancePx, opts.zoom)
  const scored = generateCandidates(point, plan, opts)
    .map(c => ({ ...c, distance: dist(point, c.point) }))
    .filter(c => Number.isFinite(c.distance) && c.distance <= tol)
  scored.sort(compareCandidates)
  return scored.map(c => ({
    point: nzVec(c.point),
    kind: c.kind,
    targetId: c.targetId,
    distance: mm(c.distance),
  }))
}

/**
 * One pass of the ranked rule: the best candidate within tolerance, or null.
 *
 * This is the literal rule from the brief. {@link snap} wraps it to reach a
 * fixed point; use this one when you want a single hop and nothing more.
 */
export function snapOnce(point: Vec2, plan: FloorPlan, opts: SnapOptions): SnapResult | null {
  const tol = worldTolerance(opts.screenTolerancePx, opts.zoom)
  let best: { kind: SnapKind; point: Vec2; targetId: string | null; distance: number } | null = null
  for (const c of generateCandidates(point, plan, opts)) {
    const d = dist(point, c.point)
    if (!Number.isFinite(d) || d > tol) continue
    const scored = { kind: c.kind, point: c.point, targetId: c.targetId, distance: d }
    if (best === null || compareCandidates(scored, best) < 0) best = scored
  }
  if (best === null) return null
  return {
    point: nzVec(best.point),
    kind: best.kind,
    targetId: best.targetId,
    distance: mm(best.distance),
  }
}

/**
 * Snap a point, ranked and IDEMPOTENT.
 *
 * A single ranked pass is NOT idempotent, and the reason is worth stating
 * because it is not obvious. Suppose the cursor lands on a grid point q, and a
 * wall endpoint e sits 0.9 tolerances from q but 1.1 tolerances from the
 * cursor. The first pass returns q (the endpoint was out of range); a second
 * pass from q returns e. `snap(snap(p)) !== snap(p)`, and the engine visibly
 * jitters when the user nudges the cursor.
 *
 * The fix is to iterate to a fixed point, and it terminates for a structural
 * reason: every kind's winning point is a distance-zero candidate OF ITS OWN
 * KIND when re-queried from there (the grid point rounds to itself, the
 * perpendicular foot re-projects to itself, the constrained angle re-constrains
 * to itself, and so on). So a further hop can only be won by a STRICTLY higher
 * priority kind, and there are only eight of those. `maxChain` is belt and
 * braces, not the mechanism.
 *
 * `distance` on the result is measured from the ORIGINAL point, which means a
 * chained result can legitimately sit further than one tolerance away.
 */
export function snap(point: Vec2, plan: FloorPlan, opts: SnapOptions): SnapResult | null {
  // Sanitised, not merely defaulted: `i < NaN` is false, so a NaN maxChain
  // would quietly turn `snap` back into a single non-idempotent pass. A finite
  // value still means what it says, floored at the one hop `snapOnce` performs.
  const rawChain = opts.maxChain
  const maxChain = typeof rawChain === 'number' && Number.isFinite(rawChain)
    ? Math.max(1, Math.floor(rawChain))
    : SNAP_KINDS.length + 1
  let current = snapOnce(point, plan, opts)
  if (current === null) return null
  for (let i = 1; i < maxChain; i++) {
    const next = snapOnce(current.point, plan, opts)
    if (next === null) break
    if (
      Math.abs(next.point.x - current.point.x) <= POLY_EPS_MM &&
      Math.abs(next.point.y - current.point.y) <= POLY_EPS_MM
    ) {
      // Stable. Keep the kind of the settled point, which may have been
      // upgraded on this pass (a grid point that is also an endpoint IS an
      // endpoint), and keep the distance measured from the original.
      current = { ...next, distance: mm(dist(point, next.point)) }
      break
    }
    current = { ...next, distance: mm(dist(point, next.point)) }
  }
  return current
}

// ---------------------------------------------------------------------------
// 4. TRANSFORMS
// ---------------------------------------------------------------------------

/** A point in CSS pixels. Y grows DOWNWARD, as on every screen. */
export interface ScreenPoint {
  readonly x: number
  readonly y: number
}

export interface Viewport {
  /** CSS pixels per world millimetre. */
  readonly zoom: number
  /** Screen x of world x = 0. */
  readonly panX: number
  /** Screen y of world y = 0. */
  readonly panY: number
}

export const IDENTITY_VIEWPORT: Viewport = Object.freeze({ zoom: 1, panX: 0, panY: 0 })

export const screenPoint = (x: number, y: number): ScreenPoint => ({ x, y })

/**
 * World millimetres (y-up) to CSS pixels (y-down).
 *
 * The minus on y is the whole handedness story for the viewport, and it is the
 * same flip the image calibration needs (see {@link calibrateFromTwoPoints}).
 */
export function worldToScreen(p: Vec2, v: Viewport): ScreenPoint {
  return { x: p.x * v.zoom + v.panX, y: v.panY - p.y * v.zoom }
}

/**
 * Exact inverse of {@link worldToScreen}.
 *
 * Zeros are canonicalised because this is a boundary where screen input turns
 * into a world coordinate that gets stored on the plan. See {@link nz}.
 */
export function screenToWorld(s: ScreenPoint, v: Viewport): Vec2 {
  if (!(v.zoom > 0) || !Number.isFinite(v.zoom)) return vec(0, 0)
  return nzVec(vec((s.x - v.panX) / v.zoom, (v.panY - s.y) / v.zoom))
}

/**
 * A screen-space delta (drag) as a world-space delta, flip included.
 *
 * The Y flip is a negation, so a purely horizontal drag produces `-0` on Y
 * without the canonicalisation. See {@link nz}.
 */
export function screenDeltaToWorld(dx: number, dy: number, v: Viewport): Vec2 {
  if (!(v.zoom > 0) || !Number.isFinite(v.zoom)) return vec(0, 0)
  return nzVec(vec(dx / v.zoom, -dy / v.zoom))
}

/** A world length in screen pixels. */
export const worldToScreenLength = (l: number, v: Viewport): number => l * v.zoom

/** A screen length in world millimetres. */
export const screenToWorldLength = (px: number, v: Viewport): number =>
  v.zoom > 0 && Number.isFinite(v.zoom) ? px / v.zoom : 0

/**
 * A 2x3 affine matrix, in the same {a,b,c,d,e,f} order as `Underlay.transform`
 * and `CanvasRenderingContext2D.setTransform`:
 *
 *   x' = a*x + c*y + e
 *   y' = b*x + d*y + f
 */
export interface AffineTransform {
  readonly a: number
  readonly b: number
  readonly c: number
  readonly d: number
  readonly e: number
  readonly f: number
}

export const IDENTITY_AFFINE: AffineTransform = Object.freeze({
  a: 1, b: 0, c: 0, d: 1, e: 0, f: 0,
})

export function applyAffine(t: AffineTransform, p: { x: number; y: number }): { x: number; y: number } {
  return { x: t.a * p.x + t.c * p.y + t.e, y: t.b * p.x + t.d * p.y + t.f }
}

export const affineDeterminant = (t: AffineTransform): number => t.a * t.d - t.b * t.c

/** True when the transform reverses handedness — which a correct image->world one must. */
export const isMirroring = (t: AffineTransform): boolean => affineDeterminant(t) < 0

/**
 * Invert a 2x3 affine, or null when it cannot be inverted.
 *
 * A zero or non-finite determinant is the obvious rejection. The less obvious
 * one is a determinant that is finite, non-zero and merely TINY: the division
 * then overflows and the "inverse" comes back full of infinities, which is a
 * far worse answer than null because it propagates silently. Every entry is
 * checked, not just the determinant.
 */
export function invertAffine(t: AffineTransform): AffineTransform | null {
  const det = affineDeterminant(t)
  if (!Number.isFinite(det) || det === 0) return null
  const inv: AffineTransform = {
    a: nz(t.d / det),
    b: nz(-t.b / det),
    c: nz(-t.c / det),
    d: nz(t.a / det),
    e: nz((t.c * t.f - t.d * t.e) / det),
    f: nz((t.b * t.e - t.a * t.f) / det),
  }
  const finite = [inv.a, inv.b, inv.c, inv.d, inv.e, inv.f].every(Number.isFinite)
  return finite ? inv : null
}

/**
 * Two-point calibration of a traced background image.
 *
 * The user clicks two points on the imported bitmap and states where they are
 * in the real world (typically by typing the distance between them along a
 * known wall). That fixes a similarity: uniform scale, rotation, translation.
 *
 * THE HANDEDNESS TRAP (docs/02 §6). Image pixels are Y-DOWN; the world is
 * Y-UP. A plain similarity fitted to two point pairs matches them exactly —
 * and so does the similarity you get by reflecting about the line through
 * them. Two point pairs cannot distinguish the two, so if the flip is not put
 * in DELIBERATELY the fit silently picks the wrong one: the underlay renders
 * mirrored, and the rotation the user calibrated turns the other way. We
 * therefore flip Y first and fit the rotation to the flipped vector, producing
 * an orientation-REVERSING similarity — its determinant is always negative.
 *
 * Returns null when either pair is degenerate (the two points coincide), which
 * carries no scale and no rotation.
 */
export function calibrateFromTwoPoints(
  imageA: ScreenPoint,
  imageB: ScreenPoint,
  worldA: Vec2,
  worldB: Vec2,
): AffineTransform | null {
  const ux = imageB.x - imageA.x
  const uy = imageB.y - imageA.y
  const vx = worldB.x - worldA.x
  const vy = worldB.y - worldA.y
  const ul = Math.hypot(ux, uy)
  const vl = Math.hypot(vx, vy)
  if (!Number.isFinite(ul) || !Number.isFinite(vl) || ul === 0 || vl === 0) return null

  const s = vl / ul
  // Flip the image vector into a y-up frame BEFORE measuring the rotation.
  const theta = Math.atan2(vy, vx) - Math.atan2(-uy, ux)
  const cos = Math.cos(theta)
  const sin = Math.sin(theta)
  // s * R(theta) * diag(1, -1)
  const a = s * cos
  const b = s * sin
  const c = s * sin
  const d = -s * cos
  return {
    a: nz(a),
    b: nz(b),
    c: nz(c),
    d: nz(d),
    e: nz(worldA.x - (a * imageA.x + c * imageA.y)),
    f: nz(worldA.y - (b * imageA.x + d * imageA.y)),
  }
}

/** Image pixel to world millimetres, through a calibration transform. */
export function imageToWorld(t: AffineTransform, p: ScreenPoint): Vec2 {
  const q = applyAffine(t, p)
  return vec(q.x, q.y)
}

/** World millimetres back to image pixels. Null when the transform is singular. */
export function worldToImage(t: AffineTransform, p: Vec2): ScreenPoint | null {
  const inv = invertAffine(t)
  if (!inv) return null
  const q = applyAffine(inv, p)
  return { x: q.x, y: q.y }
}

/** Uniform scale of a similarity transform: world mm per image pixel. */
export const calibrationScale = (t: AffineTransform): number => Math.hypot(t.a, t.b)

/**
 * Rotation of a calibration transform, in degrees CCW in the y-up world,
 * measured after the y-flip. A perfectly axis-aligned tracing returns 0.
 *
 * SIGNED, in (-180, 180], deliberately rather than normalised into [0, 360).
 * This number is read by a human calibrating a tracing that is very nearly
 * square to the page, so the common answer is a hair either side of zero — and
 * [0, 360) puts its discontinuity exactly there. Two clicks one pixel apart
 * would read as 0.0000000006 and 359.9999999994 for the same tracing. The wrap
 * belongs at half a turn, where nothing lands.
 */
export const calibrationRotationDeg = (t: AffineTransform): Deg =>
  deg(nz(radToDeg(normRad(Math.atan2(t.b, t.a)))))
